import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getCachedResponse, setCachedResponse, addPendingRequest, preloadForRole } from '../utils/offlineStore';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Configure axios
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Axios interceptors for offline caching
  const interceptorRef = useRef(null);
  useEffect(() => {
    // Response interceptor: cache GET responses in IndexedDB
    const respId = axios.interceptors.response.use(
      (response) => {
        if (response.config.method === 'get' && response.config.url) {
          const path = response.config.url.replace(process.env.REACT_APP_BACKEND_URL, '');
          setCachedResponse(path, response.data);
        }
        return response;
      },
      async (error) => {
        const config = error.config;
        // If offline and GET: try IndexedDB cache
        if (!navigator.onLine && config?.method === 'get') {
          const path = config.url?.replace(process.env.REACT_APP_BACKEND_URL, '');
          const cached = await getCachedResponse(path);
          if (cached) return { data: cached, status: 200, config, _fromCache: true };
        }
        // If offline and write operation: queue it
        if (!navigator.onLine && config && ['post', 'put', 'delete'].includes(config.method)) {
          await addPendingRequest({
            method: config.method,
            url: config.url,
            data: config.data,
            headers: { Authorization: config.headers?.Authorization }
          });
          return { data: { message: 'Sauvegarde hors-ligne, sera synchronise' }, status: 202, config, _queued: true };
        }
        return Promise.reject(error);
      }
    );
    interceptorRef.current = respId;
    return () => { axios.interceptors.response.eject(respId); };
  }, []);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    // Preload data for offline use
    setTimeout(() => preloadForRole(userData.role, axios), 1000);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Language Context
const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

const translations = {
  fr: {
    // Auth
    login: 'Connexion',
    register: "S'inscrire",
    email: 'Email',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    firstName: 'Prénom',
    lastName: 'Nom',
    phone: 'Téléphone',
    universityCode: "Code d'invitation",
    forgotPassword: 'Mot de passe oublié ?',
    noAccount: "Pas encore de compte ?",
    hasAccount: 'Déjà un compte ?',
    loginSuccess: 'Connexion réussie',
    registerSuccess: 'Inscription réussie',
    
    // Navigation
    dashboard: 'Tableau de bord',
    courses: 'Cours',
    grades: 'Notes',
    messages: 'Messages',
    calls: 'Appels',
    studyGroups: "Groupes d'etude",
    payments: 'Paiements',
    settings: 'Paramètres',
    profile: 'Profil',
    logout: 'Déconnexion',
    aiAssistant: 'Assistant IA',
    library: 'Bibliothèque',
    students: 'Étudiants',
    teachers: 'Enseignants',
    faculties: 'Facultés',
    departments: 'Départements',
    programs: 'Filières',
    promotions: 'Promotions',
    universities: 'Universités',
    users: 'Utilisateurs',
    notifications: 'Notifications',
    
    // Dashboard
    welcome: 'Bienvenue',
    totalStudents: 'Total Étudiants',
    totalTeachers: 'Total Enseignants',
    totalCourses: 'Total Cours',
    totalFaculties: 'Total Facultés',
    unreadMessages: 'Messages non lus',
    pendingPayments: 'Paiements en attente',
    creditsEarned: 'Crédits obtenus',
    myCourses: 'Mes cours',
    myGrades: 'Mes notes',
    recentActivity: 'Activité récente',
    
    // Courses
    courseCode: 'Code',
    courseName: 'Nom du cours',
    credits: 'Crédits',
    teacher: 'Enseignant',
    semester: 'Semestre',
    materials: 'Supports',
    assignments: 'Travaux',
    addMaterial: 'Ajouter un support',
    viewCourse: 'Voir le cours',
    
    // Grades
    grade: 'Note',
    average: 'Moyenne',
    session: 'Session',
    type: 'Type',
    enterGrades: 'Saisir les notes',
    viewGrades: 'Voir les notes',
    gradeHistory: 'Historique des notes',
    
    // Messages
    newMessage: 'Nouveau message',
    sendMessage: 'Envoyer',
    typeMessage: 'Écrivez votre message...',
    noMessages: 'Aucun message',
    
    // Payments
    amount: 'Montant',
    method: 'Méthode',
    status: 'Statut',
    pending: 'En attente',
    completed: 'Complété',
    failed: 'Échoué',
    makePayment: 'Effectuer un paiement',
    
    // AI
    askAI: "Poser une question à l'IA",
    generateSummary: 'Générer un résumé',
    generateQuiz: 'Générer un QCM',
    aiHelp: "Comment puis-je vous aider ?",
    
    // Common
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    add: 'Ajouter',
    search: 'Rechercher',
    filter: 'Filtrer',
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',
    confirm: 'Confirmer',
    back: 'Retour',
    next: 'Suivant',
    previous: 'Précédent',
    viewAll: 'Voir tout',
    noData: 'Aucune donnée',
    actions: 'Actions',
    date: 'Date',
    description: 'Description',
    title: 'Titre',
    name: 'Nom',
    code: 'Code',
    active: 'Actif',
    inactive: 'Inactif',
    
    // Roles
    super_admin: 'Super Administrateur',
    co_admin: 'Co-Administrateur',
    rector: 'Recteur',
    vice_rector_academic: 'Vice-Recteur Academique',
    sg_academic: 'SG Academique',
    sg_admin: 'SG Administratif',
    budget_director: 'Admin. Budget',
    dean: 'Doyen',
    vice_dean: 'Vice-Doyen',
    department_head: 'Chef de Departement',
    section_head: 'Chef de Section',
    teacher: 'Enseignant',
    visiting_teacher: 'Enseignant Visiteur',
    admin_staff: 'Personnel Administratif',
    academic_secretary: 'Secretaire Academique',
    student: 'Etudiant',
    
    // App
    appName: 'JoSmart Uni',
    tagline: 'Plateforme de gestion académique universitaire',
    selectUniversity: 'Sélectionner une université',
    selectRole: 'Sélectionner un rôle',
    offline: 'Mode hors-ligne',
    online: 'En ligne',
    sync: 'Synchroniser',
    lastSync: 'Dernière synchronisation',
  },
  en: {
    // Auth
    login: 'Login',
    register: 'Register',
    email: 'Email',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    firstName: 'First Name',
    lastName: 'Last Name',
    phone: 'Phone',
    universityCode: 'Invitation Code',
    forgotPassword: 'Forgot password?',
    noAccount: "Don't have an account?",
    hasAccount: 'Already have an account?',
    loginSuccess: 'Login successful',
    registerSuccess: 'Registration successful',
    
    // Navigation
    dashboard: 'Dashboard',
    courses: 'Courses',
    grades: 'Grades',
    messages: 'Messages',
    payments: 'Payments',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    aiAssistant: 'AI Assistant',
    calls: 'Calls',
    studyGroups: 'Study Groups',
    library: 'Library',
    students: 'Students',
    teachers: 'Teachers',
    faculties: 'Faculties',
    departments: 'Departments',
    programs: 'Programs',
    promotions: 'Promotions',
    universities: 'Universities',
    users: 'Users',
    notifications: 'Notifications',
    
    // Dashboard
    welcome: 'Welcome',
    totalStudents: 'Total Students',
    totalTeachers: 'Total Teachers',
    totalCourses: 'Total Courses',
    totalFaculties: 'Total Faculties',
    unreadMessages: 'Unread Messages',
    pendingPayments: 'Pending Payments',
    creditsEarned: 'Credits Earned',
    myCourses: 'My Courses',
    myGrades: 'My Grades',
    recentActivity: 'Recent Activity',
    
    // Courses
    courseCode: 'Code',
    courseName: 'Course Name',
    credits: 'Credits',
    teacher: 'Teacher',
    semester: 'Semester',
    materials: 'Materials',
    assignments: 'Assignments',
    addMaterial: 'Add Material',
    viewCourse: 'View Course',
    
    // Grades
    grade: 'Grade',
    average: 'Average',
    session: 'Session',
    type: 'Type',
    enterGrades: 'Enter Grades',
    viewGrades: 'View Grades',
    gradeHistory: 'Grade History',
    
    // Messages
    newMessage: 'New Message',
    sendMessage: 'Send',
    typeMessage: 'Type your message...',
    noMessages: 'No messages',
    
    // Payments
    amount: 'Amount',
    method: 'Method',
    status: 'Status',
    pending: 'Pending',
    completed: 'Completed',
    failed: 'Failed',
    makePayment: 'Make Payment',
    
    // AI
    askAI: 'Ask AI',
    generateSummary: 'Generate Summary',
    generateQuiz: 'Generate Quiz',
    aiHelp: 'How can I help you?',
    
    // Common
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    filter: 'Filter',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    viewAll: 'View All',
    noData: 'No data',
    actions: 'Actions',
    date: 'Date',
    description: 'Description',
    title: 'Title',
    name: 'Name',
    code: 'Code',
    active: 'Active',
    inactive: 'Inactive',
    
    // Roles
    super_admin: 'Super Administrator',
    co_admin: 'Co-Administrator',
    rector: 'Rector',
    vice_rector_academic: 'Academic Vice-Rector',
    sg_academic: 'Academic Secretary General',
    sg_admin: 'Admin Secretary General',
    budget_director: 'Budget Director',
    dean: 'Dean',
    vice_dean: 'Vice-Dean',
    department_head: 'Department Head',
    section_head: 'Section Head',
    teacher: 'Professor',
    visiting_teacher: 'Visiting Professor',
    admin_staff: 'Administrative Staff',
    academic_secretary: 'Academic Secretary',
    student: 'Student',
    
    // App
    appName: 'JoSmart Uni',
    tagline: 'University Academic Management Platform',
    selectUniversity: 'Select University',
    selectRole: 'Select Role',
    offline: 'Offline Mode',
    online: 'Online',
    sync: 'Sync',
    lastSync: 'Last Sync',
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'fr');

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.fr[key] || key;
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Offline Context
const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); syncNow(); };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    // Load pending count
    import('../utils/offlineStore').then(m => m.getPendingCount().then(setPendingCount));
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const { syncOfflineData, getPendingCount: getCount } = await import('../utils/offlineStore');
      const result = await syncOfflineData(axios);
      const count = await getCount();
      setPendingCount(count);
      if (result.synced > 0) {
        window.dispatchEvent(new CustomEvent('offline-synced', { detail: result }));
      }
    } catch { /* silent */ }
    finally { setSyncing(false); }
  };

  // Auto-sync when online
  useEffect(() => {
    if (isOnline && pendingCount > 0) syncNow();
  }, [isOnline]);

  return (
    <OfflineContext.Provider value={{ isOnline, pendingCount, syncing, syncNow }}>
      {children}
    </OfflineContext.Provider>
  );
};

// Theme Context (for university customization)
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    primaryColor: '#0F2C59',
    secondaryColor: '#D4AF37',
    logoUrl: null
  });
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('josmart_dark_mode');
    return saved === 'true';
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('josmart_dark_mode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  const updateTheme = (newTheme) => {
    setTheme(prev => ({ ...prev, ...newTheme }));
    document.documentElement.style.setProperty('--tenant-primary', newTheme.primaryColor || theme.primaryColor);
    document.documentElement.style.setProperty('--tenant-secondary', newTheme.secondaryColor || theme.secondaryColor);
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};

export { API_URL };

